import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { CloudFunctions } from '../../utils/enums/CloudFunctions';
import { getFunctions, httpsCallable } from 'firebase/functions';

const TOKEN_STORAGE_KEY = 'googleCalendarToken';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export const GoogleCalendarContext = createContext(null);

export const GoogleCalendarProvider = ({ children }) => {
  const [isGoogleConnected, setIsGoogleConnected] = useState(false);
  const [googleAccessToken, setGoogleAccessToken] = useState(null);
  const [externalEvents, setExternalEvents] = useState([]);
  const [tokenClient, setTokenClient] = useState(null);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const syncGoogleCalendar = useCallback(async (accessToken) => {
    try {
      const now = new Date();
      // Get events from 1 year ago to 2 years in the future
      const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()).toISOString();
      const twoYearsFromNow = new Date(now.getFullYear() + 2, now.getMonth(), now.getDate()).toISOString();

      const response = await fetch(
        `https://www.googleapis.com/calendar/v3/calendars/primary/events?singleEvents=true&timeMin=${oneYearAgo}&timeMax=${twoYearsFromNow}&maxResults=2500`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      if (response.status === 401) {
        const newToken = await refreshGoogleToken();
        return newToken ? syncGoogleCalendar(newToken) : [];
      }

      const data = await response.json();
      return data.items
        .filter(event => !event.status?.includes('cancelled')) // Filter out cancelled events
        .map((event) => ({
          id: event.id,
          title: event.summary || 'Untitled Event',
          start: event.start?.dateTime || event.start?.date || null,
          end: event.end?.dateTime || event.end?.date || null,
          url: event.htmlLink || '',
          description: event.description || '',
          location: event.location || '',
          extendedProps: {
            creator: event.creator?.displayName || event.creator?.email || 'Unknown',
            recurrence: event.recurrence || null,
            attendees: event.attendees || [],
            source: 'google',
            originalId: event.id,
            status: event.status,
            transparency: event.transparency, // Free/busy status
            visibility: event.visibility, // Public/private status
            updated: event.updated,
            created: event.created,
          },
        }));
    } catch (error) {
      console.error('Error syncing calendar events:', error);
      return [];
    }
  }, []);

  const fetchHolidayEvents = useCallback(async (accessToken, calendarId = 'en.usa#holiday@group.v.calendar.google.com') => {
    try {
      const response = await fetch(
        `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events?singleEvents=true`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
        }
      );

      if (response.status === 401) {
        const newToken = await refreshGoogleToken();
        return newToken ? fetchHolidayEvents(newToken, calendarId) : [];
      }

      const data = await response.json();
      return data.items.map((event) => ({
        id: event.id,
        title: event.summary || 'Holiday',
        start: event.start?.dateTime || event.start?.date || null,
        end: event.end?.dateTime || event.end?.date || null,
        url: event.htmlLink || '',
        extendedProps: {
          creator: 'Google Holidays',
        },
      }));
    } catch (error) {
      console.error('Error fetching holiday events:', error);
      return [];
    }
  }, []);

  const syncCalendar = useCallback(async () => {
    if (!googleAccessToken) return;
    
    try {
      setIsLoading(true);
      // First check if token is expired
      const storedTokenData = JSON.parse(localStorage.getItem(TOKEN_STORAGE_KEY) || '{}');
      if (storedTokenData.expiresAt <= Date.now()) {
        const newToken = await refreshGoogleToken();
        if (!newToken) return;
      }

      const [calendarEvents, holidayEvents] = await Promise.all([
        syncGoogleCalendar(googleAccessToken),
        fetchHolidayEvents(googleAccessToken)
      ]);

      // Process the events to ensure we don't have duplicates
      const processedEvents = calendarEvents.map(event => {
        // Check if this event already exists in Firebase
        const existingFirebaseEvent = externalEvents.find(e => 
          e.extendedProps?.googleEventId === event.id || 
          (e.title === event.title && 
           e.start === event.start && 
           e.end === event.end)
        );

        if (existingFirebaseEvent) {
          return {
            ...event,
            id: existingFirebaseEvent.id, // Keep the Firebase ID
            extendedProps: {
              ...event.extendedProps,
              firebaseId: existingFirebaseEvent.id,
              isLinked: true
            }
          };
        }

        return event;
      });

      const combined = [...processedEvents, ...holidayEvents];
      setExternalEvents(combined);
      setLastSyncTime(new Date().toISOString());

      // Store the sync time in localStorage
      localStorage.setItem('lastCalendarSync', new Date().toISOString());
    } catch (error) {
      console.error('Error syncing calendar:', error);
    } finally {
      setIsLoading(false);
    }
  }, [googleAccessToken, syncGoogleCalendar, fetchHolidayEvents, externalEvents]);

  // Add automatic sync on mount and periodic sync
  useEffect(() => {
    if (googleAccessToken) {
      // Get last sync time
      const lastSync = localStorage.getItem('lastCalendarSync');
      const now = new Date();
      
      // If no last sync or it's been more than 5 minutes, sync
      if (!lastSync || (now - new Date(lastSync)) > 5 * 60 * 1000) {
        syncCalendar();
      }
    }
  }, [googleAccessToken, syncCalendar]);

  // Initialize Google API
  useEffect(() => {
    const initializeGoogle = () => {
      if (window.google) {
        const client = window.google.accounts.oauth2.initCodeClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: 'https://www.googleapis.com/auth/calendar.readonly https://www.googleapis.com/auth/calendar.events',
          access_type: 'offline',
          prompt: 'consent',
          callback: handleGoogleCallback,
        });
        setTokenClient(client);
      }
    };

    const loadGoogleScript = () => {
      if (!document.querySelector('#google-oauth')) {
        const script = document.createElement('script');
        script.id = 'google-oauth';
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = initializeGoogle;
        document.head.appendChild(script);
      } else {
        initializeGoogle();
      }
    };

    const checkStoredToken = async () => {
      const storedTokenData = JSON.parse(localStorage.getItem(TOKEN_STORAGE_KEY) || '{}');
      if (storedTokenData.token) {
        if (storedTokenData.expiresAt - Date.now() < 5 * 60 * 1000) {
          if (storedTokenData.refreshToken) {
            await refreshGoogleToken();
          } else {
            localStorage.removeItem(TOKEN_STORAGE_KEY);
            return;
          }
        }
        setGoogleAccessToken(storedTokenData.token);
        setIsGoogleConnected(true);
      }
    };

    checkStoredToken();
    loadGoogleScript();
  }, []);

  const addToGoogleCalendar = async (eventData) => {
    if (!googleAccessToken) return null;

    try {
      const response = await fetch('https://www.googleapis.com/calendar/v3/calendars/primary/events', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${googleAccessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          summary: eventData.title,
          description: eventData.description,
          start: {
            dateTime: eventData.start.toISOString(),
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
          },
          end: {
            dateTime: eventData.end.toISOString(),
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
          },
          attendees: eventData.attendees || [],
          location: eventData.location || '',
          reminders: {
            useDefault: true
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create Google Calendar event');
      }

      const googleEvent = await response.json();
      return googleEvent.id;
    } catch (error) {
      console.error('Error creating Google Calendar event:', error);
      return null;
    }
  };

  const updateGoogleEvent = async (eventData, originalEvent) => {
    try {
      // Handle date conversion
      const getDateTime = (date) => {
        if (date && date.toDate) {
          return date.toDate().toISOString();
        } else if (date instanceof Date) {
          return date.toISOString();
        } else {
          return new Date(date).toISOString();
        }
      };

      const response = await fetch(
        `https://www.googleapis.com/calendar/v3/calendars/primary/events/${originalEvent.extendedProps.originalId}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${googleAccessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            summary: eventData.title,
            description: eventData.description,
            start: {
              dateTime: getDateTime(eventData.start),
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
            },
            end: {
              dateTime: getDateTime(eventData.end),
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
            }
          })
        }
      );

      if (!response.ok) {
        console.error('Failed to update Google Calendar event:', await response.text());
        return { success: false, error: 'Failed to update Google Calendar event' };
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating Google Calendar event:', error);
      return { success: false, error };
    }
  };

  const deleteGoogleEvent = async (eventId) => {
    if (!googleAccessToken) return false;

    try {
      const response = await fetch(
        `https://www.googleapis.com/calendar/v3/calendars/primary/events/${eventId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${googleAccessToken}`
          }
        }
      );

      return response.ok;
    } catch (error) {
      console.error('Error deleting Google Calendar event:', error);
      return false;
    }
  };

  const handleGoogleCallback = async (response) => {
    if (response.code) {
      try {
        const functions = getFunctions();
        const exchangeTokenFn = httpsCallable(functions, CloudFunctions.ExchangeGoogleToken);
        
        const result = await exchangeTokenFn({ 
          code: response.code,
          redirectUri: window.location.origin 
        });

        if (result.data.success) {
          const { accessToken, refreshToken, expiresIn } = result.data.data;
          const tokenData = {
            token: accessToken,
            refreshToken: refreshToken,
            expiresAt: Date.now() + expiresIn * 1000,
          };
          localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokenData));
          setGoogleAccessToken(accessToken);
          setIsGoogleConnected(true);
        } else {
          console.error('Failed to exchange token:', result.data);
        }
      } catch (error) {
        console.error('Error exchanging token:', error);
      }
    }
  };

  const refreshGoogleToken = async () => {
    try {
      const storedTokenData = JSON.parse(localStorage.getItem(TOKEN_STORAGE_KEY) || '{}');
      if (!storedTokenData.refreshToken) {
        throw new Error('No refresh token available');
      }

      const functions = getFunctions();
      const refreshTokenFn = httpsCallable(functions, CloudFunctions.RefreshGoogleToken);
      const response = await refreshTokenFn({ refreshToken: storedTokenData.refreshToken });

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to refresh token');
      }

      const tokenData = {
        token: response.data.data.accessToken,
        refreshToken: storedTokenData.refreshToken,
        expiresAt: Date.now() + response.data.data.expiresIn * 1000,
      };

      localStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokenData));
      setGoogleAccessToken(response.data.data.accessToken);
      return response.data.data.accessToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      handleGoogleDisconnect();
      return null;
    }
  };

  const handleGoogleDisconnect = () => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    setGoogleAccessToken(null);
    setIsGoogleConnected(false);
    setExternalEvents([]);
    setLastSyncTime(null);
  };

  const handleGoogleSignIn = () => {
    tokenClient?.requestCode();
  };

  // Add this new effect for token refresh
  useEffect(() => {
    let refreshInterval;
    
    const scheduleTokenRefresh = () => {
      const storedTokenData = JSON.parse(localStorage.getItem(TOKEN_STORAGE_KEY) || '{}');
      if (storedTokenData.expiresAt) {
        // Refresh 5 minutes before expiration
        const timeUntilRefresh = storedTokenData.expiresAt - Date.now() - (5 * 60 * 1000);
        if (timeUntilRefresh > 0) {
          refreshInterval = setTimeout(async () => {
            await refreshGoogleToken();
            scheduleTokenRefresh(); // Schedule next refresh
          }, timeUntilRefresh);
        } else {
          // If already expired or close to expiring, refresh immediately
          refreshGoogleToken().then(() => scheduleTokenRefresh());
        }
      }
    };

    if (isGoogleConnected) {
      scheduleTokenRefresh();
    }

    return () => {
      if (refreshInterval) clearTimeout(refreshInterval);
    };
  }, [isGoogleConnected]);

  return (
    <GoogleCalendarContext.Provider value={{
      isGoogleConnected,
      googleAccessToken,
      externalEvents,
      handleGoogleSignIn,
      handleGoogleDisconnect,
      lastSyncTime,
      isLoading,
      addToGoogleCalendar,
      updateGoogleEvent,
      deleteGoogleEvent,
      syncCalendar
    }}>
      {children}
    </GoogleCalendarContext.Provider>
  );
};

export const useGoogleCalendarContext = () => {
  const context = useContext(GoogleCalendarContext);
  if (!context) {
    throw new Error('useGoogleCalendarContext must be used within a GoogleCalendarProvider');
  }
  return context;
}; 